import { GenericServices } from "./generic.services";
import videoUri from "../common/uri/video-uri.constants";
import { ApiResponse } from "../models/app-configuration";

export interface VideoSearchPayload {
    PageIndex: number;
    PageSize: number;
    OrderBy?: string;
    FilterCondition?: {
        Fields: Array<{ Name: string; Value: string; DataType?: number }>;
    };
    [key: string]: any;
}

export interface UploadVideoResponse {
    VideoUid: string;
    VideoName: string;
    AccountId: string;
    Thumbnail: string;
    VideoIframe: string;
    Size: number;
    Duration: number;
    CreateDate: string;
    ModifiedDate?: string;
    UploadedDate?: string;
    [key: string]: any;
    Configuration?: any;
}

export interface VideoStreamStatusResponse {
    VideoUid: string;
    VideoName: string;
    Thumbnail: string;
    OrignalVideoUrl: string;
    VideoPreview: string;
    VideoIframe: string;
    Size: number;
    Duration: number;
    CreateDate: string;
    ModifiedDate: string;
    UploadedDate: string;
    ReadyToStream: boolean;
    Error: string;
    RequireSignedURLs: boolean;
    AllowedOrigins: string[];
}

export class VideoManagementService extends GenericServices {
    async searchVideos(payload: VideoSearchPayload) {
        try {
            const response: any= await this.executeSelectingPost<VideoSearchPayload, any>(payload, videoUri.search);
            if (response?.isSuccess) {
                return response.data;
            } else {
                throw new Error(response?.message);
            }
        } catch (error) {
            console.error(error);
            throw error;
        }
    }

    saveVideo(payload: any) {
        return this.executeCreating<any, any>(payload, videoUri.save);
    }

    updateVideo(id: number, payload: any) {
        const path = videoUri.update.replace("{videoUid}", id.toString());
        return this.executePatch(payload, path);
    }

    deleteVideo(id: string) {
        return this.executeDeleting<ApiResponse>(videoUri.delete.replace('{id}', id));
    }

    async getVideoStreamStatus(videoUid: string): Promise<VideoStreamStatusResponse> {
        const path = videoUri.streamStatus.replace("{videoUid}", videoUid);
        const response: any = await this.executeSelecting<void, any>(undefined, path);
        if (response?.isSuccess) {
            return response.data;
        }
        throw new Error(response?.message || "Failed to get video stream status");
    }

    async waitForVideoReady(
        videoUid: string,
        options: { maxAttempts?: number; intervalMs?: number } = {}
    ): Promise<VideoStreamStatusResponse> {
        const { maxAttempts = 30, intervalMs = 5000 } = options;
        let attempts = 0;

        while (attempts < maxAttempts) {
            try {
                const status = await this.getVideoStreamStatus(videoUid);
                if (status.ReadyToStream) {
                    return status;
                }
                if (status.Error) {
                    throw new Error(status.Error);
                }
            } catch (error) {
                if (attempts >= maxAttempts - 1) {
                    throw error;
                }
            }
            attempts++;
            await new Promise(resolve => setTimeout(resolve, intervalMs));
        }

        throw new Error("Video processing timeout");
    }

}
